import {IHierarchyRelationship, IHierarchyStructure} from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import * as _ from "lodash";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {AuditService} from "../PA.Core/Security/AuditService";
import {AuditLogType} from "../PA.Core/PA.Core.IF.Audit";
import {ErrorMessage_restate, ExceptionMessage_restate} from "../PA.Core/Basic/MessageUtils";
import {HierarchyHarness} from "./HierarchyHarness";

export interface IExistingRelsItem {
    rels: IHierarchyRelationship[],
    error: boolean,
    sErrors: string[],
    processed: boolean
}

export class HierarchyUpdateUtils {

    static async syncFullHierarchy(src: IHierarchyStructure,
                                   tgt: IHierarchyStructure,
                                   options: { messageProvider?: IMessageProvider, progressProvider?: IProgressProvider }): Promise<void> {

        let opts = options || {}
        let mp: IMessageProvider = undefined
        let pp: IProgressProvider = undefined

        if ('progressProvider' in opts)
            pp = opts?.progressProvider

        if (_.isEmpty(pp))
            pp = new ProgressProvider("Syncing Hierarchy")

        if ('messageProvider' in opts)
            mp = opts.messageProvider

        if (_.isEmpty(mp))
            mp = new MessageProvider()


        try {
            await src.OnLoadComplete()
            await tgt.OnLoadComplete()

            pp.start()
            mp.clearMessages()
            // Update target hierarchy using Logical Key
            // Each target row should be unique by (Child WbId, valid from).. if the target has dups, report as warnings? errors ?
            // verify source is correctly unique by (Child WbId, valid from).. if the source has dups, report as warnings? errors ?
            // Delete extra,dups in target
            // Create missing, update relations as needed

            // Basic algorithm,
            //  index the target relationships
            function populateDct(results: { [mapper: string]: { [wbId: number]: { [Start_Dt: string]: IHierarchyRelationship } } }, relRow: IHierarchyRelationship) {
                let dct: any = results
                let rel: any = relRow
                let mapper = rel.child.mapperName
                let wbId = rel.child.wbId
                let start_dt = DateUtils.convertToSAPBW(rel.effectiveStartDt)
                if (!(mapper in dct))
                    dct[mapper] = {}
                if (!(wbId in dct[mapper]))
                    dct[mapper][wbId] = {}
                if (!(start_dt in dct[mapper][wbId]))
                    dct[mapper][wbId][start_dt] = rel
                return dct
            }

            let dctSource: { [mapper: string]: { [wbId: number]: { [Start_Dt: string]: IHierarchyRelationship } } } = _.reduce(src.Relations, function (results, relRow) {
                return populateDct(results, relRow)
            }, {})
            let dctTarget: { [mapperId: string]: { [wbId: number]: { [Start_Dt: string]: IHierarchyRelationship } } } = _.reduce(tgt.Relations, function (results, relRow) {
                return populateDct(results, relRow)
            }, {})


            // find relations in target not updated, and delete..
            function bFoundRel(rel: IHierarchyRelationship) {
                if (!(rel.child.mapperName in dctSource))
                    return false
                let dctL1 = dctSource[rel.child.mapperName] || {}
                if (!(rel.child.wbId in dctL1))
                    return false
                let dctL2 = dctL1[rel.child.wbId]
                if (!(DateUtils.convertToSAPBW(rel.effectiveStartDt) in dctL2))
                    return false
                return true
            }

            for (let tgtRel of tgt.Relations) {
                if (!bFoundRel(tgtRel))
                    tgtRel.Delete()
                else {
                    if (!DateUtils.isEqual(tgtRel.effectiveEndDt, dctSource[tgtRel.child.mapperName][tgtRel.child.wbId][DateUtils.convertToSAPBW(tgtRel.effectiveStartDt)].effectiveEndDt)) {
                        tgtRel.setEffectiveEndDt(dctSource[tgtRel.child.mapperName][tgtRel.child.wbId][DateUtils.convertToSAPBW(tgtRel.effectiveStartDt)].effectiveEndDt)
                    }
                    if (dctSource[tgtRel.child.mapperName][tgtRel.child.wbId][DateUtils.convertToSAPBW(tgtRel.effectiveStartDt)].parent != tgtRel.parent) {
                        tgtRel.setParent(dctSource[tgtRel.child.mapperName][tgtRel.child.wbId][DateUtils.convertToSAPBW(tgtRel.effectiveStartDt)].parent)
                    }
                    dctSource[tgtRel.child.mapperName][tgtRel.child.wbId][DateUtils.convertToSAPBW(tgtRel.effectiveStartDt)].Delete()
                }
            }

            // iterate over source relations, updating/ creating target relations
            function addRelToDct(srcRel: IHierarchyRelationship) {
                if (!(srcRel.child.mapperName in dctTarget))
                    dctTarget[srcRel.child.mapperName] = {}
                if (!(srcRel.child.wbId in dctTarget[srcRel.child.mapperName]))
                    dctTarget[srcRel.child.mapperName][srcRel.child.wbId] = {}
                if (!(DateUtils.convertToSAPBW(srcRel.effectiveStartDt) in dctTarget[srcRel.child.mapperName][srcRel.child.wbId])) {
                    dctTarget[srcRel.child.mapperName][srcRel.child.wbId][DateUtils.convertToSAPBW(srcRel.effectiveStartDt)] = srcRel
                }

            }

            for (let srcRel of _.filter(src.Relations, aRel => aRel.isActive)) {
                if ((_.isEmpty(dctTarget) || _.isEmpty(dctTarget[srcRel.child.mapperName]) || _.isEmpty(dctTarget[srcRel.child.mapperName][srcRel.child.wbId]) || _.isEmpty(dctTarget[srcRel.child.mapperName][srcRel.child.wbId][DateUtils.convertToSAPBW(srcRel.effectiveStartDt)]))) {
                    let cr: IHierarchyRelationship = await tgt.createRelation()
                    cr.setChild(srcRel.child)
                    cr.setParent(srcRel.parent)
                    cr.setEffectiveStartDt(srcRel.effectiveStartDt)
                    cr.setEffectiveEndDt(srcRel.effectiveEndDt)
                    addRelToDct(srcRel)
                }
            }
            pp.finish()

        } catch (error) {
            mp.addMessage(
                {
                    code: 'LoadError',
                    type: MessageType.Error,
                    title: 'Error occurs when loading hierarchy:' + error,
                    description: "",
                    subtitle: "",
                    count: 0
                })
        }
    }

    static async applyPeriodSyncHierarchy(src: IHierarchyStructure,
                                          srcStartDt: Date,
                                          srcEndDt: Date,
                                          tgt: IHierarchyStructure,
                                          options: { messageProvider?: IMessageProvider, progressProvider?: IProgressProvider, bEndDateUnknown?: boolean })
        : Promise<void> {
        let mp: IMessageProvider = undefined
        let pp: IProgressProvider = undefined
        let opts = options || {}

        let bEndDateUnknown = !!opts.bEndDateUnknown

        pp = opts?.progressProvider

        if (_.isEmpty(pp))
            pp = new ProgressProvider("Updating Hierarchy")

        mp = opts?.messageProvider
        if (_.isEmpty(mp))
            mp = new MessageProvider()


        try {
            pp.start()
            mp.clearMessages()

            if (!src.bLoaded)
                await src.OnLoadComplete()
            if (!tgt.bLoaded)
                await tgt.OnLoadComplete()

            if (!src.bLoaded) {
                ErrorMessage_restate(mp, true, "SrcLoadError", "Source Hierarchy isn't Loaded")
                return
            }

            if (src.bHasEffectiveDates) {
                ErrorMessage_restate(mp, true, "SrcInputError", "Source Hierarchy can't have Effective Dates when Merging")
                return
            }

            if (!tgt.bLoaded) {
                ErrorMessage_restate(mp, true, "TgtLoadError", "Target Hierarchy isn't Loaded")
                return
            }


            // Create Dictionary of Source Nodes By Mapper/ ChildWbId
            let dctSrc: { [mapperName: string]: { [wbId: number]: { rel: IHierarchyRelationship, error: boolean, dupCount: number } } } = {}

            // Create Dictionary of Target Relationships by Mapper/ ChildWbId:
            let dctTgt: { [mapperName: string]: { [wbId: number]: IExistingRelsItem } } = {}

            let ecParentNodeMissing = 0
            let ecChildNodeMissing = 0
            let ecDuplicateRels = 0

            for (let rel of src.Relations) {
                if (!rel.isActive)
                    continue

                if (_.isEmpty(rel.child)) {
                    ecChildNodeMissing++
                    continue
                }


                let mapperName = rel.child.mapperName
                let wbId = rel.child.wbId

                if (!(mapperName in dctSrc))
                    dctSrc[mapperName] = {}
                if (!(wbId in dctSrc[mapperName]))
                    dctSrc[mapperName][wbId] = {rel: rel, error: false, dupCount: 0}
                else {
                    ecDuplicateRels++
                    dctSrc[mapperName][wbId].dupCount++
                }

                //--

            }

            ErrorMessage_restate(mp, ecChildNodeMissing > 0,
                "SrcChildEmpty", `Source Hierarchy has ${ecChildNodeMissing} relationships with empty child`)
            ErrorMessage_restate(mp, ecDuplicateRels > 0,
                "SrcDupChildRels", `Source Hierarchy has ${ecDuplicateRels} duplicates by child`)

            //-- Load Target Hierarchy
            ecChildNodeMissing = 0
            let ecRelDatesEmpty = 0
            let ecRelDateOrder = 0

            let tgtErrorRels: IHierarchyRelationship[] = []

            for (let rel of tgt.Relations) {
                let bError = false
                if (!rel.isActive)
                    continue

                if (_.isEmpty(rel.child)) {
                    ecChildNodeMissing++
                    tgtErrorRels.push(rel)
                    continue
                }


                if ((!_.isDate(rel.effectiveStartDt))
                    || (!_.isDate(rel.effectiveEndDt))) {
                    ecRelDatesEmpty++
                    bError = true
                } else if (DateUtils.isGreaterThan(rel.effectiveStartDt, rel.effectiveEndDt)) {
                    ecRelDateOrder++
                    bError = true
                }

                if (bError)
                    tgtErrorRels.push(rel)

                let mapperName = rel.child.mapperName
                let wbId = rel.child.wbId

                if (!(mapperName in dctTgt))
                    dctTgt[mapperName] = {}
                if (!(wbId in dctTgt[mapperName]))
                    dctTgt[mapperName][wbId] = {rels: [rel], error: bError, sErrors: [], processed: false}
                else {
                    let ri = dctTgt[mapperName][wbId]
                    ri.rels.push(rel)
                    ri.error ||= bError
                }

                //--

            }

            ErrorMessage_restate(mp, ecChildNodeMissing > 0,
                "TgtChildEmpty", `Target Hierarchy has ${ecChildNodeMissing} relationships with empty child`)
            ErrorMessage_restate(mp, ecRelDatesEmpty > 0,
                "TgtDateEmpty", `Target Hierarchy has ${ecRelDatesEmpty} relationships with empty dates`)
            ErrorMessage_restate(mp, ecRelDateOrder > 0,
                "TgtDateOrder", `Target Hierarchy has ${ecRelDateOrder} relationships with dates out of order`)

            // Preprocess Target Rels
            _.forEach(dctTgt, (subDct, mapperName) => {
                _.forEach(subDct, (a, wbId) => {
                    HierarchyUpdateUtils.validateAndSort(a, mp)
                })
            })

            // Apply updates for Known Child Records in Source
            for (let mapperName in dctSrc) {
                let subDct = dctSrc[mapperName]
                for (let wbId in subDct) {
                    let src = subDct[wbId]
                    let tgtRi = (dctTgt[mapperName] || {})[wbId]
                    if (_.isEmpty(tgtRi))
                        await HierarchyUpdateUtils.addRelToTarget(tgt, src.rel, srcStartDt, srcEndDt, mp)
                    else
                        await HierarchyUpdateUtils.applyMergeUpdate(tgt, src.rel, srcStartDt, srcEndDt, tgtRi, mp)
                }
            }

            // Find the unprocessed and terminate any relationships
            for(let mapperName in dctTgt) {
                let subDct = dctTgt[mapperName]

                for(let wbId in subDct) {
                    let ri = subDct[wbId]
                    if (ri.processed || ri.error)
                        continue

                    if(!bEndDateUnknown)
                        continue

                    // Do terminate Rels not in data set
                    await HierarchyUpdateUtils.endDateInactiveNodes(tgt, ri, srcStartDt, srcEndDt, mp)
                }
            }


            pp.finish()

        } catch (error) {
            ExceptionMessage_restate(mp, error)
        } finally {
            // Audit Log all messages
            let as = new AuditService()
            as.setReference("Hierarchy", tgt.wbHierarchyId)
            as.LogSync(AuditLogType.Info, "Hierarchy Update - Merging updates")
            for (let aMsg of mp.Messages) {
                as.LogMsgSync(aMsg)
            }
            await as.Save()
        }


        return
    }

    static validateAndSort(ri: IExistingRelsItem, mp: IMessageProvider) {

        //-- Skip if there is only 1 rel or earlier errors
        if ((ri.rels.length == 1) || ri.error)
            return

        // Sort Rels by Effective Start Dt
        ri.rels = _.orderBy(ri.rels, (a) => a.effectiveStartDt)

        // Check for overlapping dates, and fix?
        let lastRel = undefined
        _.forEach(ri.rels, (r, idx) => {
            let nextRel = ri.rels[idx + 1]
            if (_.isEmpty(nextRel)) { // Last Relationship
                // Force an End Date to 12/31/9999
            } else {
                if (DateUtils.RangeDatesOverlap(r.effectiveStartDt, r.effectiveEndDt, nextRel.effectiveStartDt, nextRel.effectiveEndDt))
                    ri.error = true

                // Check for date continuity?

            }

            lastRel = r
        })
    }

    static async applyMergeUpdate(tgt: IHierarchyStructure, srcRel: IHierarchyRelationship, startDt: Date, endDt: Date, eri: IExistingRelsItem, mp: IMessageProvider) {
        //--
        eri.processed = true
        let relFound = false
        let tgtRel: IHierarchyRelationship
        let bIsTopNode = _.isEmpty(srcRel.parent)

        // Check case where start Date exists before first rel
        let firstRel = eri.rels[0]
        if (DateUtils.isLessThan(startDt, firstRel.effectiveStartDt)) {
            relFound = true
            tgtRel = await tgt.createRelation()
            tgtRel.setChild(srcRel.child)
            tgtRel.setParent(srcRel.parent)
            tgtRel.setEffectiveStartDt(startDt)
            tgtRel.setEffectiveEndDt(endDt)
        }

        // For Each Source Item, (which are sorted by Date)
        //--
        //-- if rel found, && endDt < rel.startDt, then break, no other updates
        //-- if rel found, && endDt > rel.endDt, then delete relationship
        //-- if rel found, else  rel.startDate = endDate + 1

        //-- if rel not found, find first rel that contains startDt
        for (let rel of eri.rels) {
            let breakLoop = false

            switch (true) {
                case relFound && DateUtils.isLessThan(endDt, rel.effectiveStartDt):
                    breakLoop = true
                    break

                case relFound && DateUtils.isGreaterThan(endDt, rel.effectiveEndDt):
                    rel.Delete()
                    break

                case relFound: // endDt in current rel..
                    rel.setEffectiveStartDt(DateUtils.addDays(endDt, 1))
                    break

                case DateUtils.InRangeDates(startDt, rel.effectiveStartDt, rel.effectiveEndDt):
                    // If the Parent is the same,
                    //          then potentially extend the End Date of Rel, if needed
                    // If the Parent is different,
                    //          then insert new relationship
                    //          end date current relationship
                    //          if the current relationship start on same date, then delete
                    let bParentMatch = false
                    if (bIsTopNode)
                        bParentMatch = _.isEmpty(rel.parent)
                    else {
                        let srcParentWbId = srcRel?.parent?.wbId || -1
                        let tgtParentWbId =rel?.parent?.wbId || -1
                        bParentMatch = (srcParentWbId == tgtParentWbId)
                    }
                    if (bParentMatch) {
                        relFound = true
                        tgtRel = rel
                        if (DateUtils.isGreaterThan(endDt, rel.effectiveEndDt))
                            rel.setEffectiveEndDt(endDt)
                    } else {
                        //--Different Parent
                        relFound = true
                        let tgtRel = await tgt.createRelation()
                        tgtRel.setChild(srcRel.child)
                        tgtRel.setParent(srcRel.parent)
                        tgtRel.setEffectiveStartDt(startDt)
                        tgtRel.setEffectiveEndDt(endDt)

                        // Terminate existing rel
                        rel.setEffectiveEndDt(DateUtils.addDays(startDt, -1))

                        // If start date=end date, then delete relationship
                        if (DateUtils.isEqual(rel.effectiveStartDt, startDt))
                            rel.Delete()
                    }
                    break
                default:
                    // skipping relationship not overlapping
                    break;
            }

            if (breakLoop)
                break
        }

        if (!relFound) {
            // Create Rel at End
            let tgtRel = await tgt.createRelation()
            tgtRel.setChild(srcRel.child)
            tgtRel.setParent(srcRel.parent)
            tgtRel.setEffectiveStartDt(startDt)
            tgtRel.setEffectiveEndDt(endDt) // could use 9999-12-31

        }

    }

    static async endDateInactiveNodes(tgt: IHierarchyStructure, eri: IExistingRelsItem, startDt: Date, endDt: Date, mp: IMessageProvider) {
        //--
        eri.processed = true


        // For Each Source Item, (which are sorted by Date)
        //--
        //-- if rel end date is before "end date" period, leave alone
        //-- if rel starts on or after the "end date" period, delete
        //-- otherwise, rel straddles start date, set end date to startDt - 1
        //

        //--
        for (let rel of eri.rels) {

            switch (true) {

                // Rel exists before end date period, no change
                case DateUtils.isLessThan(rel.effectiveEndDt, startDt):
                    //Leave it alone
                    break

                // Rel starts after the "end date period" start date, remove rel
                case DateUtils.isLessThanEqual(startDt, rel.effectiveStartDt):
                    rel.Delete()
                    break

                // Rel straddles start date, so end date relationship.
                default:
                    rel.setEffectiveEndDt(DateUtils.addDays(startDt, -1))
                    break

            }
        }

    }


    static async addRelToTarget(tgt: IHierarchyStructure, srcRel: IHierarchyRelationship, startDt: Date, endDt: Date, mp: IMessageProvider) {
        //--
        let rel = await tgt.createRelation()
        rel.setChild(srcRel.child)
        rel.setParent(srcRel.parent)
        rel.setEffectiveStartDt(DateUtils.convertFromSAPBW("18000101"))
        rel.setEffectiveEndDt(DateUtils.convertFromSAPBW("99991231"))
    }


    static async synchronizeSalesAreas(tgt: IHierarchyStructure, harness: HierarchyHarness): Promise<void> {

        if (!tgt.bLoaded)
            return

        if (!tgt.bHasSalesAreas)
            return

        let arrSAs = _.map(
            _.filter(harness.WB_HierarchyRow.WB_HierarchySalesAreaRows, (a) => a.isActive && (a.cif.WB_SALESAREA_ID > 0)),
            (a) => a.cif.WB_SALESAREA_ID)
        let dctSAs = _.keyBy(arrSAs, (a) => a)
        let dctSAsToAdd = _.clone(dctSAs)

        // Remove Extra
        _.forEach(tgt.SalesAreas, (sa) => {
            if (sa.wbSalesAreaId in dctSAs) {
                delete dctSAsToAdd[sa.wbSalesAreaId]
            } else {
                sa.Delete()
            }
        })
        for (let wbSAId of _.values(dctSAsToAdd)) {
            let rsa = await tgt.createSalesArea(wbSAId)
        }

    }

    static async synchronizeLevelsFromHarness(tgt: IHierarchyStructure, harness: HierarchyHarness, bSkipLoadedCheck?: boolean): Promise<void> {

        if ((!tgt.bLoaded) && (!bSkipLoadedCheck))
            return

        if (!tgt.bHasLevels)
            return

        let arrLevels = _.filter(harness.WB_HierarchyRow.WB_HierarchyLevelRows,
            (a) => a.isActive && (!_.isEmpty(a.cif.LEVEL_ID)))

        let dctLevels = _.keyBy(arrLevels, (a) => a.cif.LEVEL_ID)
        let dctLevelsToAdd = _.clone(dctLevels)

        // Remove Extra, Sync Existing
        _.forEach(tgt.Levels, (lvl) => {
            if(!lvl.isActive)
                return

            if (lvl.levelId in dctLevels) {
                // sync
                let srcLvl = dctLevels[lvl.levelId]
                lvl.setLevelDepth(srcLvl.cif.LEVEL_DEPTH)
                lvl.setLevelName(srcLvl.cif.LEVEL_NAME)
                lvl.setLevelUnique(srcLvl.bUnique)
                lvl.setLevelEntityType(srcLvl.cif.WB_ENTITY_ID)
                lvl.setLevelEnterpriseEntityType(srcLvl.cif.WB_ENTITY_ID_ENTERPRISE)
                delete dctLevelsToAdd[lvl.levelId]
            } else {
                lvl.Delete()
            }
        })
        for (let lvlRow of _.values(dctLevelsToAdd)) {
            let lvl = await tgt.createLevel(lvlRow.cif.LEVEL_ID)
            lvl.setLevelId(lvlRow.cif.LEVEL_ID)
            lvl.setLevelDepth(lvlRow.cif.LEVEL_DEPTH)
            lvl.setLevelName(lvlRow.cif.LEVEL_NAME)
            lvl.setLevelUnique(lvlRow.bUnique)
            lvl.setLevelEntityType(lvlRow.cif.WB_ENTITY_ID)
            lvl.setLevelEnterpriseEntityType(lvlRow.cif.WB_ENTITY_ID_ENTERPRISE)
        }

    }

    static async synchronizeLevels(src: IHierarchyStructure, tgt: IHierarchyStructure): Promise<void> {

        if (!src?.bLoaded)
            return

        if (!tgt?.bLoaded)
            return

        if (!tgt.bHasLevels)
            return

        if (!tgt.bHasLevels)
            return

        let arrLevels = _.filter(src.Levels,
            (a) => a.isActive && (!_.isEmpty(a.levelId)))

        let dctLevels = _.keyBy(arrLevels, (a) => a.levelId)
        let dctLevelsToAdd = _.clone(dctLevels)

        // Remove Extra, Sync Existing
        _.forEach(tgt.Levels, (lvl) => {
            if(!lvl.isActive)
                return

            if (lvl.levelId in dctLevels) {
                // sync
                let srcLvl = dctLevels[lvl.levelId]
                lvl.setLevelDepth(srcLvl.levelDepth)
                lvl.setLevelName(srcLvl.levelName)
                lvl.setLevelUnique(srcLvl.levelUnique)
                lvl.setLevelEntityType(srcLvl.levelEntityTypeId)
                lvl.setLevelEnterpriseEntityType(srcLvl.levelEnterpriseEntityTypeId)
                delete dctLevelsToAdd[lvl.levelId]
            } else {
                lvl.Delete()
            }
        })
        for (let srcLvlRow of _.values(dctLevelsToAdd)) {
            let tgtLvl = await tgt.createLevel(srcLvlRow.levelId)
            tgtLvl.setLevelId(srcLvlRow.levelId)
            tgtLvl.setLevelDepth(srcLvlRow.levelDepth)
            tgtLvl.setLevelName(srcLvlRow.levelName)
            tgtLvl.setLevelUnique(srcLvlRow.levelUnique)
            tgtLvl.setLevelEntityType(srcLvlRow.levelEntityTypeId)
            tgtLvl.setLevelEnterpriseEntityType(srcLvlRow.levelEnterpriseEntityTypeId)
        }

    }


}